import { useEffect, useState, type ReactNode } from 'react';
import { getEmailMessage } from '@lib/care-api';
import type { EmailView } from '@lib/care-model';
import { fmtTs } from '@lib/care-model';

/**
 * PART Q (William, 17 Oct 2026 12:25 AM, on WY Blue email case EVENT-20261015-144858-1f36: "can you provide the
 * email message for ability to determine who the person might be"): an Email case shows the whole message.
 * From (display name and address), To, Sent, Subject, the full body as text (HTML converted on the server), the
 * quoted chain folded under "Show earlier messages", and the attachment names. A message with a subject only says
 * so in words. Read only; the server reads the case's raw copy (GET /api/care/event/email).
 *
 * NO em dashes (house voice rule). ASCII hyphens only.
 */
export const EMAIL_WORDING = {
  from: 'From',
  to: 'To',
  cc: 'Cc',
  subject: 'Subject',
  attachments: 'Attachments',
  empty: 'The email had a subject only, no message text.',
  showEarlier: 'Show earlier messages',
  hideEarlier: 'Hide earlier messages',
  loading: 'Loading the email...',
  unread: 'The email could not be read just now; the stored text is shown.',
  fromRecord: 'The original email could not be opened, so the stored copy is shown (it has no display name or recipients).',
} as const;

/** "Tom Example <bluebird77@gmail.com>", or the address alone. */
export function fromWords(v: Pick<EmailView, 'fromName' | 'fromAddress'>): string {
  return v.fromName ? `${v.fromName} <${v.fromAddress}>` : v.fromAddress;
}

export function EmailMessageView({ view }: { view: EmailView }) {
  const [showEarlier, setShowEarlier] = useState(false);
  const row = (k: string, v: string, testId: string) => (
    <div className="leader-row" data-testid={testId}>
      <span className="k">{k}</span>
      <span className="leader" />
      <span className="v" style={{ whiteSpace: 'normal', textAlign: 'right' }}>{v}</span>
    </div>
  );
  return (
    <div className="small" data-testid="email-message">
      {row(EMAIL_WORDING.from, fromWords(view), 'email-from')}
      {view.to.length > 0 && row(EMAIL_WORDING.to, view.to.join(', '), 'email-to')}
      {view.cc.length > 0 && row(EMAIL_WORDING.cc, view.cc.join(', '), 'email-cc')}
      {view.sent && row(view.sentLabel, fmtTs(view.sent), 'email-sent')}
      {row(EMAIL_WORDING.subject, view.subject || '(no subject)', 'email-subject')}
      <div style={{ marginTop: 8, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }} data-testid="email-body">
        {view.empty ? <span className="muted">{EMAIL_WORDING.empty}</span> : view.body || <span className="muted">(no new text above the earlier messages)</span>}
      </div>
      {view.earlier && (
        <div style={{ marginTop: 8 }}>
          <button type="button" className="btn btn-ghost btn-sm" data-testid="email-earlier-toggle" onClick={() => setShowEarlier((o) => !o)}>
            {showEarlier ? EMAIL_WORDING.hideEarlier : EMAIL_WORDING.showEarlier}
          </button>
          {showEarlier && (
            <div
              className="muted"
              data-testid="email-earlier"
              style={{ marginTop: 6, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', borderLeft: '2px solid var(--paper-3)', paddingLeft: 8 }}
            >
              {view.earlier}
            </div>
          )}
        </div>
      )}
      {view.attachments.length > 0 && (
        <div style={{ marginTop: 8 }} data-testid="email-attachments">
          <span className="micro">{EMAIL_WORDING.attachments}: </span>
          {view.attachments.join(', ')}
        </div>
      )}
      {view.source === 'record' && <div className="micro muted" style={{ marginTop: 6 }}>{EMAIL_WORDING.fromRecord}</div>}
    </div>
  );
}

/** Loads the case's email and shows it; on a failed read the caller's fallback (the stored text) is shown. */
export function EmailMessage({ eventId, fallback }: { eventId: string; fallback: ReactNode }) {
  const [view, setView] = useState<EmailView | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let live = true;
    setView(null);
    setFailed(false);
    getEmailMessage(eventId)
      .then((v) => { if (live) setView(v); })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, [eventId]);
  if (failed) {
    return (
      <>
        <div className="micro muted" style={{ marginBottom: 4 }}>{EMAIL_WORDING.unread}</div>
        {fallback}
      </>
    );
  }
  if (!view) return <div className="small muted">{EMAIL_WORDING.loading}</div>;
  return <EmailMessageView view={view} />;
}
